import { useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { byMarketplaceRevenue } from "../lib/marketplace-revenue-order"
import { useMarketplaceSummaries } from "../services/use-dashboard"
import { useRates } from "../services/use-rates"

/**
 * Entry point for the campaign table: a pass-through to one marketplace's page,
 * never a page of its own — the same shape `/pnl` and `/storage-refresh` use.
 * A chooser page would cost two clicks to reach any table, and switching belongs
 * on the table itself.
 *
 * Lands on the BIGGEST channel by revenue, the same order the switcher lists
 * (`byMarketplaceRevenue`). A marketplace that declares no advertiser has no
 * table; the section says so rather than the landing choice trying to predict it,
 * which would take an ad read per marketplace.
 */
function CampaignsIndexPage() {
  const summaries = useMarketplaceSummaries()
  const rates = useRates()
  const navigate = useNavigate()
  // Rates decide the order, so the landing choice must wait for them: redirecting
  // first and resorting later would drop the operator on the wrong table.
  const ranked = rates.isLoading
    ? []
    : [...(summaries.data ?? [])].sort(byMarketplaceRevenue(rates.data))
  const landing = ranked[0]?.marketplace

  useEffect(() => {
    if (landing) {
      void navigate({
        to: "/campaigns/$marketplace",
        params: { marketplace: landing },
        replace: true,
      })
    }
  }, [landing, navigate])

  return (
    <AppShell
      title="Campaigns"
      description="Advertised products and the two pairs a bid is decided from."
    >
      <QueryState
        // Redirecting counts as still loading: rendering an empty shell in the
        // frame before the navigation lands reads as "no data".
        isLoading={summaries.isLoading || rates.isLoading || landing !== undefined}
        error={summaries.error}
        isEmpty={summaries.data?.length === 0}
        emptyMessage="No marketplace is configured yet."
      >
        {null}
      </QueryState>
    </AppShell>
  )
}

export const Route = createFileRoute("/campaigns/")({ component: CampaignsIndexPage })
